import {
  contactErrorCodes,
  contactSubmissionSchema,
  firstContactError,
  type ContactErrorCode,
  type ContactResponse,
  type ContactStatus,
} from '@shared/contact-schema';

/**
 * Browser-side helpers for the contact form. The Worker validates again and is
 * the authority; this only lets the page point at the right field sooner.
 */

export type ContactField = 'name' | 'email' | 'organization' | 'message';
export type ClientErrorCode = ContactErrorCode | 'network';

export interface ContactFormError {
  code: ClientErrorCode;
  field?: ContactField;
}

export function fieldForError(code: ClientErrorCode): ContactField | undefined {
  if (code.startsWith('name_')) return 'name';
  if (code.startsWith('email_')) return 'email';
  if (code.startsWith('organization_')) return 'organization';
  if (code.startsWith('message_')) return 'message';
  return undefined;
}

export interface ContactValues {
  name: string;
  email: string;
  organization: string;
  message: string;
  website: string;
  locale: 'en' | 'es';
}

export function validateContact(values: ContactValues): ContactFormError | null {
  const parsed = contactSubmissionSchema.safeParse(values);
  if (parsed.success) return null;
  const code = firstContactError(parsed.error);
  return { code, field: fieldForError(code) };
}

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** Whether the server can deliver messages. Unknown (null) when it could not be asked. */
export async function fetchContactStatus(fetcher: Fetch): Promise<boolean | null> {
  try {
    const response = await fetcher('/api/contact', { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const status = (await response.json()) as Partial<ContactStatus>;
    return typeof status.enabled === 'boolean' ? status.enabled : null;
  } catch {
    return null;
  }
}

/**
 * An error code from the API, if it is one this page has a message for;
 * anything else (an unknown or newer code, a proxy's error body) is shown as
 * the generic "could not send" message instead of a missing translation key.
 */
export function knownErrorCode(code: unknown): ContactErrorCode {
  return typeof code === 'string' && (contactErrorCodes as readonly string[]).includes(code)
    ? (code as ContactErrorCode)
    : 'send_failed';
}

export async function submitContact(fetcher: Fetch, values: ContactValues): Promise<ContactFormError | null> {
  let response: Response;
  try {
    response = await fetcher('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(values),
    });
  } catch {
    return { code: 'network' };
  }
  const result = (await response.json().catch(() => null)) as ContactResponse | null;
  if (response.ok && result?.ok) return null;
  const code = knownErrorCode(result && !result.ok ? result.error : undefined);
  return { code, field: fieldForError(code) };
}
